import { Link } from 'gatsby';
import * as React from 'react';
import * as staticPaths from '../../staticPaths';

type FAQContentItem = {
  question: React.ReactNode;
  answer: React.ReactNode;
};

const faqsDefault: FAQContentItem[] = [
  {
    question: "What's included in the trial?",
    answer:
      'In your 7-day free trial, you can create one room, one roster, and one seating chart. You can print your seating chart as needed, though prints will include a watermark during the trial period.',
  },
  {
    question: 'Are there organizational/business/team plans available?',
    answer:
      "Yes, we offer team plans that allow multiple users and shared resources. If you're interested, please contact us.",
  },
  {
    question: 'How can I import a list of names? What formats are supported?',
    answer: (
      <>
        You have two options for importing rosters:
        <ol>
          <li>
            Direct Upload: Upload your roster using standard spreadsheet formats
            like CSV or Excel.
          </li>
          <li>
            Copy & Paste: Alternatively, copy and paste a list of names into the
            app's input field. Seating Chart Maker will automatically detect
            separator characters such as newlines and commas.
          </li>
        </ol>
        <p>
          After importing, you can modify the name format and add photos. For
          the copy-and-paste method, if you cannot copy directly from your
          source, you may want to export your data first to a file before
          copying.
        </p>
      </>
    ),
  },
  {
    question: 'Can I create a chart manually?',
    answer:
      'Absolutely! You can modify the automatically created chart or manually start from scratch to seat participants.',
  },
  {
    question: 'How do I add participant photos?',
    answer:
      'You can add photos to your roster by uploading them directly from your computer. Click the avatar icon next to the name in the roster view to upload a photo.',
  },
  {
    question:
      'Can I pause my subscription? I only need to create seating plans every few months.',
    answer: (
      <p>
        We don't offer a pause feature, but you can cancel and restart your
        subscription anytime. Don't worry about losing your data; we keep it
        safe for 12 months after you unsubscribe. This means you can return and
        continue where you left off, with all your seating plans and data ready
        for your next period.
      </p>
    ),
  },
  {
    question:
      'Do I need to install anything? What are the system requirements for Seating Chart Maker?',
    answer: (
      <>
        <p>
          No installation is required. Seating Chart Maker is a web-based
          application that works in any modern web browser, including Google
          Chrome, Mozilla Firefox, Safari, and Microsoft Edge. It also works on
          mobile devices and tablets, although we recommend using a desktop or
          laptop for the best experience.
        </p>
      </>
    ),
  },
  {
    question:
      'Can I create multiple seating charts for the same room and roster-combination?',
    answer:
      'Yes, you can. You can reuse room and roster data to create different seating charts. For example, you can use a single room to create seating charts for multiple events held in the same room.',
  },
  {
    question:
      'What export options does Seating Chart Maker offer for my seating charts and room templates?',
    answer:
      'Seating Chart Maker allows you to export your seating charts and room templates in various formats, including PDF and image formats in different sizes. You can also print your seating charts directly from the app.',
  },
  {
    question:
      'Can I present my seating plans from my computer in fullscreen? Can I flip the perspective when printing or presenting?',
    answer: (
      <>
        <p>
          When presenting from your computer, you can use the app's built-in
          full-screen mode to display your seating chart.
        </p>
        <p>
          You can also flip the perspective between the presenter and audience
          perspective when viewing, presenting, or printing. Perspective
          flipping is useful for organizers who want to present seating charts
          to the audience from their point of view. All names and labels will
          remain readable and oriented in the correct direction.
        </p>
      </>
    ),
  },
  {
    question: 'I can not access my account. What should I do?',
    answer: (
      <>
        <p>
          If you no longer have access to your account, please{' '}
          <Link
            to={staticPaths.contact}
            className="font-medium text-indigo-600 hover:text-indigo-500"
          >
            contact us
          </Link>
          . We'll be happy to help you regain access by, for example, changing
          your email address after verifying that you are the account owner.
        </p>
      </>
    ),
  },
];

const faqsClassroom: FAQContentItem[] = [
  {
    question: "What's included in the trial?",
    answer:
      'In your 7-day free trial, you can create one room, one roster, and one seating chart. You can print your seating chart as needed, though prints will include a watermark during the trial period.',
  },
  {
    question: 'Are there business/team/school plans available?',
    answer:
      "Yes, we do offer team plans that allow multiple users and shared resources. If you're interested, please get in touch with us.",
  },
  {
    question: 'How can I import a student list? What formats are supported?',
    answer: (
      <>
        You have two convenient options for importing rosters:
        <ol>
          <li>
            Direct Upload: Easily upload your roster directly using common
            spreadsheet formats like CSV or Excel.
          </li>
          <li>
            Copy & Paste: Alternatively, copy and paste a list of names into our
            input field. Seating Chart Maker will automatically detect separator
            characters such as newlines and commas.
          </li>
        </ol>
        <p>
          After importing, you can easily modify the name format and add student
          photos. For the copy & paste method, if you cannot copy directly from
          your source, you may want to export your data to a CSV, Excel, or
          similar format before copying.
        </p>
      </>
    ),
  },
  {
    question: 'Can I create a plan manually with the generator?',
    answer:
      'Absolutely! You can modify the automatically created chart or start from scratch to place students manually.',
  },
  {
    question: 'Can I tweak a random seating plan manually?',
    answer:
      'Yes! You can modify the randomized seating chart with easy drag & drop. You can also re-randomize the seating plan with one click, or set rules for the automatic seating. You can, for example, keep certain participants together or apart, or select to leave possible empty chairs at the back of the room.',
  },
  {
    question: 'How do I add a student photo?',
    answer:
      "You can add student photos to your roster by uploading them directly from your computer. Just click the avatar icon next to the student's name in the roster view to upload a photo.",
  },
  {
    question:
      'Can I pause my subscription? I only need to create seating plans at the beginning of a new period.',
    answer: (
      <p>
        We don't offer a pause feature, but you can cancel and restart your
        subscription anytime. Don't worry about losing your data; we keep it
        safe for 12 months after you unsubscribe. This means you can come back
        and continue where you left off, with all your seating plans and data
        ready for your next period.
      </p>
    ),
  },
  {
    question:
      'Do I need to install anything? What are the system requirements for Seating Chart Maker?',
    answer: (
      <>
        <p>
          No installation is required. Seating Chart Maker is a web-based
          application that works in any modern web browser. This includes Google
          Chrome, Mozilla Firefox, Safari, and Microsoft Edge. It also works on
          mobile devices and tablets, although we recommend using a desktop or
          laptop for the best experience.
        </p>
      </>
    ),
  },
  {
    question:
      'Can I create multiple seating charts for the same classroom and student list?',
    answer:
      'Yes, you can. You have the option to reuse room and roster data to create different seating charts. For example, you can use a single room template to create seating charts for multiple classes.',
  },
  {
    question:
      'What export options does Seating Chart Maker offer for my seating charts and empty classroom templates?',
    answer:
      'Seating Chart Maker allows you to export your seating charts and room templates in a variety of formats. This includes PDF for easy printing and distribution, and image formats for convenient digital display and sharing.',
  },
  {
    question:
      'Can I print the seating plans? Can I present them from my computer? Can I flip the perspective when printing or presenting?',
    answer: (
      <>
        <p>
          Yes, you can print your seating plans or room templates. Seating Chart
          Maker allows you to export these to PDF in common paper sizes (like
          Letter and A4) for easy printing. You can also export to image formats
          for digital display, sharing, or custom printing.
        </p>
        <p>
          When presenting from your computer, you can use the built-in
          full-screen mode from the app to display your seating chart.
        </p>
        <p>
          You can also flip the perspective between teacher and student
          perspective when viewing, presenting or printing. This is useful for
          teachers who want to present seating charts to students from their own
          point of view. All names and labels will remain readable and oriented
          in the correct direction.
        </p>
      </>
    ),
  },
  {
    question: 'I can not access my account. What should I do?',
    answer: (
      <>
        <p>
          If you no longer have access to your account, please{' '}
          <Link
            to={staticPaths.contact}
            className="font-medium text-indigo-600 hover:text-indigo-500"
          >
            contact us
          </Link>{' '}
          and we'll be happy to help you regain access by, for example, changing
          your email address after verifying you're the account owner.
        </p>
      </>
    ),
  },
];

const faqsWedding: FAQContentItem[] = [
  {
    question: "What's included in the trial?",
    answer:
      'In your 7-day free trial, you can create one room, one roster, and one seating chart. You can print your seating chart as needed, though prints will include a watermark during the trial period.',
  },
  {
    question: 'Are there organizational/business/team plans available?',
    answer:
      "Yes, we offer team plans that allow multiple users and shared resources. If you're interested, please contact us.",
  },
  {
    question: 'How can I import a list of names? What formats are supported?',
    answer: (
      <>
        You have two options for importing rosters:
        <ol>
          <li>
            Direct Upload: Upload your roster using standard spreadsheet formats
            like CSV or Excel.
          </li>
          <li>
            Copy & Paste: Alternatively, copy and paste a list of names into the
            app's input field. Seating Chart Maker will automatically detect
            separator characters such as newlines and commas.
          </li>
        </ol>
        <p>
          After importing, you can modify the name format and add guest photos.
          For the copy-and-paste method, if you cannot copy directly from your
          source, you may want to export your data first to a file before
          copying.
        </p>
      </>
    ),
  },
  {
    question: 'Can I create a chart manually?',
    answer:
      'Absolutely! You can modify the automatically created chart or manually start from scratch to seat participants.',
  },
  {
    question: 'How do I add participant photos?',
    answer:
      "You can add photos to your roster by uploading them directly from your computer. Click the avatar icon next to the guest's name in the roster view to upload a photo.",
  },
  {
    question:
      'Can I pause my subscription? I only need to create seating plans every few months.',
    answer: (
      <p>
        We don't offer a pause feature, but you can cancel and restart your
        subscription anytime. Don't worry about losing your data; we keep it
        safe for 12 months after you unsubscribe. This means you can return and
        continue where you left off, with all your seating plans and data ready
        for your next period.
      </p>
    ),
  },
  {
    question:
      'Do I need to install anything? What are the system requirements for Seating Chart Maker?',
    answer: (
      <>
        <p>
          No installation is required. Seating Chart Maker is a web-based
          application that works in any modern web browser, including Google
          Chrome, Mozilla Firefox, Safari, and Microsoft Edge. It also works on
          mobile devices and tablets, although we recommend using a desktop or
          laptop for the best experience.
        </p>
      </>
    ),
  },
  {
    question:
      'Can I create multiple seating charts for the same room and roster-combination?',
    answer:
      'Yes, you can. You can reuse venue and guest data to create different seating charts. For example, you can use a single venue layout to create seating charts for multiple events held in the same place.',
  },
  {
    question:
      'What export options does Seating Chart Maker offer for my seating charts and room templates?',
    answer:
      'Seating Chart Maker allows you to export your seating charts and room templates in various formats, including PDF and image formats in different sizes. You can also print your seating charts directly from the app.',
  },
  {
    question:
      'Can I present my seating plans from my computer in fullscreen? Can I flip the perspective when printing or presenting?',
    answer: (
      <>
        <p>
          When presenting from your computer, you can use the app's built-in
          full-screen mode to display your seating chart.
        </p>
        <p>
          You can also flip the perspective between the presenter and audience
          perspective when viewing, presenting, or printing. Perspective
          flipping is useful for organizers who want to present seating charts
          to the audience from their point of view. All names and labels will
          remain readable and oriented in the correct direction.
        </p>
      </>
    ),
  },
  {
    question: 'I can not access my account. What should I do?',
    answer: (
      <>
        <p>
          If you no longer have access to your account, please{' '}
          <Link
            to={staticPaths.contact}
            className="font-medium text-indigo-600 hover:text-indigo-500"
          >
            contact us
          </Link>
          . We'll be happy to help you regain access by, for example, changing
          your email address after verifying that you are the account owner.
        </p>
      </>
    ),
  },
];

const FAQ: React.FC<{ faqs: FAQContentItem[] }> = ({ faqs }) => (
  <div className="bg-white">
    <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:py-20 lg:px-8">
      <div className="lg:grid lg:grid-cols-3 lg:gap-8">
        <div>
          <h2 className="text-3xl font-extrabold text-gray-900">
            Frequently asked questions
          </h2>
          <p className="mt-4 text-lg text-gray-500">
            Can’t find the answer you’re looking for? Reach out to our{' '}
            <Link
              to={staticPaths.contact}
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              customer support
            </Link>{' '}
            team.
          </p>
        </div>
        <div className="mt-12 lg:mt-0 lg:col-span-2">
          <dl className="space-y-12">
            {faqs.map((faq, i) => (
              <div key={i}>
                <dt className="text-lg leading-6 font-medium text-gray-900">
                  {faq.question}
                </dt>
                <dd className="mt-2 text-base text-gray-500 prose">
                  {faq.answer}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  </div>
);

export const FAQDefault = () => <FAQ faqs={faqsDefault} />;
export const FAQClassroom = () => <FAQ faqs={faqsClassroom} />;
export const FAQWedding = () => <FAQ faqs={faqsWedding} />;
